<template>
  <div
    class="escape-presenting"
    @click.stop="handleBacktoEditor()"
    :class="{ hidden: hideBackButton }"
  >
    <div
      class="escape-presenting __wrapper"
      :class="{'light-text': isTextColourLight && !isPreview}"
      @mouseover="handleMouseOver()"
      @mouseleave="handleMouseLeave()"
    >
      <aha-icon
        name="arrow-tail"
        viewBox="0 0 18 19"
        width="21"
        height="22"
        v-if="!loading"
      />
      <aha-spinner v-if="loading" :variant="getColorSpinner" class="loading-icon" />
    </div>
    <div class="tooltip-escape" v-show="showTooltip">
      <div
        class="tooltip-text"
        :class="{
          'is-not-preview' : !isPreview
        }"
      >
        {{ $i18nPresenter.t(isZoomReady ? 'Back' : 'Back to Editor') }}
      </div>
      <div
        class="tooltip__tip"
        :class="{
          'is-not-preview': !isPreview
        }"
      ></div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import utils from '@/util'
import SlideApi from '@/api/slide'
import PresentationApi from '@/api/presentation'
import constants from '@/constant'
import Socket from '@/socket'
import Sentry from '@/services/sentry'
import { INPUT_METHODS } from '@/constant/inputMethods.constants'
import EventBus from '@/eventBus'
import office from '@/services/office'
import Config from '@/config'

export default {
  props: {
    source: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showTooltip: false,
      loading: false,
      hideBackButton: false,
    }
  },
  computed: {
    ...mapGetters('zoomApp', ['isZoomReady']),
    ...mapGetters('presentationHotKeyModule', ['hotKeyTrackingData']),
    ...mapGetters(['isTextColourLight']),
    ...mapState({
      presentation: (state) => state.presentation,
      slideActive: (state) => state.slideActive,
      isAdmin: (state) => state.isAdmin,
      sharePresentation: (state) => state.sharePresentation,
      isPreview: (state) => state.isPreview,
      isEnableAddinPowerpoint: (state) => state.isEnableAddinPowerpoint,
      integrationPlatform: (state) => state.integrationPlatform,
    }),
    conditionsUpdateLastSlide() {
      return this.presentation.id && !this.isAdmin && !this.sharePresentation
    },
    getColorSpinner() {
      if (this.source === 'ppt-no-presenting') return 'dark'
      if (this.isTextColourLight) return 'white'
      return 'dark'
    },
  },
  mounted() {
    EventBus.$on('close-presenting-window', () => {
      this.handleBacktoEditor()
    })

    if (utils.getParamValue('presentFrom') === 'google-slide') {
      this.hideBackButton = true
    }
  },
  destroyed() {
    EventBus.$off('close-presenting-window')
  },
  methods: {
    ...mapActions(['setSyncLoadingAction', 'toggleQRCodePanelAction']),
    async handleBacktoEditor() {
      if (this.isEnableAddinPowerpoint) {
        this.loading = true
        await office.saveDataToSlidePowerPoint('dataSlidePPT', null)
        office.setDataSlideInPowerpointToState(null, null)
        office.setDataPresentationInPowerpointToState(null)
        if (this.conditionsUpdateLastSlide) {
          await this._updatePresentationLastSlide()
        }
        setTimeout(() => {
          window.location.href = `${Config.presenterDomain}/presentation/${this.presentation.id}`
        }, 300)
        return
      }
      if (this.isPreview) {
        this.$emit('exitPreviewMode')
        return
      }
      if (this.isZoomReady) {
        this.$router.push({
          name: 'ZoomPresentationList',
        })
        return
      }
      // stop presenting
      utils.setCurrentWindowMode()
      if (this.integrationPlatform === 'GoogleSlide') {
        this.$router.replace({
          name: 'PresentationDetailGoogleSlide',
          params: { id: this.presentation.id },
        })
      } else {
        this.$router.replace({
          name: 'PresentationDetail',
          params: { id: this.presentation.id },
        })
      }

      if (this.slideActive.type == 'pickAnswer' && !this.isAdmin) {
        this._updateSlideQuizStatus()
      }

      if (this.conditionsUpdateLastSlide) {
        this._updatePresentationLastSlide()
      }
      this.trackButtonBackToEditor()
      EventBus.$emit('close-backstage-window')
      this.toggleQRCodePanelAction(false)
    },
    trackButtonBackToEditor() {
      const eventName = 'Stop presenting button > Stop presenting'
      const {
        eventCategory,
        presentationId,
        userId,
      } = this.hotKeyTrackingData
      utils.mixpanelTracking(eventCategory, eventName, null, null, {
        presentationId,
        userId,
        method: INPUT_METHODS.MOUSE,
      })
    },
    handleMouseOver() {
      this.showTooltip = true
    },
    handleMouseLeave() {
      this.showTooltip = false
    },
    async _updateSlideQuizStatus() {
      try {
        await SlideApi.updateSlideById(this.slideActive.id, {
          quizStatus: constants.QuizStatuses.LOBBY_SCREEN,
          socketId: Socket.getSocketId(),
          accessCode: this.presentation.uniqueAccessCode,
          activeSlide: this.slideActive.id,
          presentationId: this.presentation.id,
          presenting: false,
        })
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncLoadingAction(3)
      }
    },

    _getPayloadUpdatePresentationLastSlide() {
      const idLastSlide = utils.getLastSlide(
        this.$store.state.presentation.slides,
      )
      const payload = {
        presenting: false,
        socketId: Socket.getSocketId(),
        activeSlidePreseting: this.slideActive.id,
        activeSlideAccessCode: this.presentation.uniqueAccessCode,
      }
      if (idLastSlide && idLastSlide != this.presentation.lastSlide) {
        payload.lastSlide = idLastSlide
        this.presentation.lastSlide = idLastSlide
      }
      return payload
    },

    async _updatePresentationLastSlide() {
      try {
        const payload = this._getPayloadUpdatePresentationLastSlide()
        await PresentationApi.updatePresentationByPresentationId(this.presentation.id, payload)
      } catch (error) {
        Sentry.captureException(error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.escape-presenting {
  position: absolute;
  cursor: pointer;
  top: 0.25rem;
  left: 0.5rem;
  font-size: 1.4rem;
  z-index: 135;
  opacity: 0;
  transition: all 0.3s ease;
  &.__wrapper {
    @apply rounded-full flex items-center justify-center w-10 h-10 bg-base-30 hover:bg-base-40;
    backdrop-filter: blur(44px);
    &.light-text {
      background-color: rgba(0, 0, 0, 0.36);
    }
    &:hover.light-text {
      background-color: rgba(0, 0, 0, 0.5);
    }
  }
}
.tooltip-escape {
  transition: opacity 0.2s ease;
  .tooltip-text {
    background-color: #282e3e;
    color: #ffffff;
    text-align: center;
    border-radius: 4px;
    padding-top: 0.35rem;
    padding-bottom: 0.35rem;
    padding-right: 0.5rem;
    padding-left: 0.5rem;
    font-size: 12px;
    position: absolute;
    font-family: "Rubik", sans-serif;
    font-style: normal;
    font-weight: 400;
    top: 3.5rem;
    &.is-not-preview {
      position: relative;
      top: 3.25rem;
    }
  }

  .tooltip__tip {
    position: absolute;
    left: 2.5rem;
    width: 7px;
    height: 7px;
    z-index: 10;
    font-size: 14px;
    background: #282E3E;
    transform: translate(-50%, 0) rotate(45deg);
    border-bottom-right-radius: 2px;
    outline: none;
    &.is-not-preview {
      position: relative;
      width: 11px !important;
      height: 11px !important;
      top: 1.25rem;
      right: calc(50% - 11px) !important;
    }
  }
}
</style>
